/**
 * 换行标签模式（传给 options.lineBreakMode）
 * @readonly
 */
const ADD_BR_LINE_BREAK_MODES = {
  /** 仅 PC 显示换行：`<br class="normal-small_hide">` */
  PC: 'pc',
  /** 仅移动端显示换行：`<br class="normal-pc_hide">` */
  MOBILE: 'mobile',
  /** 全端普通换行：`<br>` */
  PLAIN: 'plain',
};

/**
 * 按目标行数插入 <br>，使各行字符数尽量接近。
 * @param {string} text - 原始文本（无复杂 HTML 时最可预期）
 * @param {number} lineCount - 希望分成的行数（>=1）
 * @param {{
 *   preferBreakChars?: string,
 *   lineBreakMode?: 'pc' | 'mobile' | 'plain',
 *   lineBreakFor?: 'pc' | 'mobile' | 'mb',
 *   brClass?: string
 * }} [options]
 * @param {string} [options.preferBreakChars] - 优先断行字符集
 * @param {'pc'|'mobile'|'plain'} [options.lineBreakMode] - 三种换行：`pc` | `mobile` | `plain`（纯 `<br>`）。默认 `pc`
 * @param {'pc'|'mobile'|'mb'} [options.lineBreakFor] - 兼容旧参：等同 `lineBreakMode`，`mb` 视为 `mobile`
 * @param {string} [options.brClass] - 自定义 class，若设置则覆盖 `lineBreakMode`
 * @returns {string} 插入 <br> 后的 HTML 片段（需配合 unescaped 输出）
 */
function addBrForText(text, lineCount, options) {
  if (text == null || text === '') return '';
  const n = Math.max(1, Math.floor(Number(lineCount)) || 1);
  if (n < 1) return String(text);

  const brTag = resolveBrTag(options);

  const raw = String(text);
  const prefer =
    (options && options.preferBreakChars) ||
    ' \t\n\r，。、；：！？,.;:!?-—·|/\\';

  const len = raw.length;
  if (len <= n) {
    return raw.split('').join(brTag);
  }

  const parts = [];
  let start = 0;

  for (let lineIndex = 0; lineIndex < n - 1; lineIndex++) {
    const remainingLines = n - lineIndex;
    const remainingLen = len - start;
    const idealEnd = start + remainingLen / remainingLines;
    let end = Math.round(idealEnd);

    if (end <= start) end = start + 1;
    if (end >= len) end = len - (remainingLines - 1);
    if (end <= start) {
      parts.push(raw.slice(start));
      start = len;
      break;
    }

    const searchLow = Math.max(start + 1, end - Math.ceil(remainingLen * 0.15));
    const searchHigh = Math.min(len - (remainingLines - 1), end + Math.ceil(remainingLen * 0.15));

    let breakAt = end;
    let best = -1;
    let bestDist = Infinity;
    for (let i = searchLow; i <= searchHigh; i++) {
      if (!prefer.includes(raw[i - 1])) continue;
      const dist = Math.abs(i - idealEnd);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      } else if (dist === bestDist && i > best) {
        // 距离相同优先稍靠后，避免过早截断
        best = i;
      }
    }
    if (best > start) {
      breakAt = best;
    }

    parts.push(raw.slice(start, breakAt));
    start = breakAt;
  }

  if (start < len) {
    parts.push(raw.slice(start));
  }

  return parts.join(brTag);
}

/**
 * @param {{
 *   lineBreakMode?: string,
 *   lineBreakFor?: string,
 *   brClass?: string
 * }} [options]
 * @returns {string} 完整 <br> 标签
 */
function resolveBrTag(options) {
  if (options && typeof options.brClass === 'string') {
    const c = options.brClass.trim();
    if (c) {
      return `<br class="${c.replace(/"/g, '&quot;')}">`;
    }
  }

  const rawMode =
    (options && options.lineBreakMode) ||
    (options && options.lineBreakFor) ||
    ADD_BR_LINE_BREAK_MODES.PC;

  const mode = rawMode === 'mb' ? ADD_BR_LINE_BREAK_MODES.MOBILE : rawMode;

  if (mode === ADD_BR_LINE_BREAK_MODES.PLAIN || mode === 'plain') {
    return '<br>';
  }
  if (mode === ADD_BR_LINE_BREAK_MODES.MOBILE || mode === 'mobile') {
    return '<br class="normal-pc_hide">';
  }
  return '<br class="normal-small_hide">';
}

if (typeof window !== 'undefined') {
  window.addBrForText = addBrForText;
  window.ADD_BR_LINE_BREAK_MODES = ADD_BR_LINE_BREAK_MODES;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    addBrForText,
    resolveBrTag,
    ADD_BR_LINE_BREAK_MODES,
  };
}
